import React, { useRef } from 'react';

import Icon from '../ui-components/Icon';
import { getFaro } from 'flow/api-clients/faro-client';
import { addGeneralLogEntry } from 'flow/utils/browser-utils';
import { removeNotAllowedDTMFCharacter } from 'flow/utils/call-utils';
import { autoUpdate, useFloating, shift } from '@floating-ui/react';
import ClickAwayListener from 'flow/components/ClickAwayListener';
import IconButton from '../ui-components/IconButton';
import useAppContext from 'flow/hooks/useAppContext';

interface Props {
    handleSendDtmf: (symbol: string) => void;
    handleClose: () => void;
    number: string;
    setNumber: (input: string) => void;
    anchorEl: any;
}

interface ButtonsProps {
    clickHandler: (e: React.MouseEvent, digit: string) => void;
}

const DialpadButtons = ({ clickHandler }: ButtonsProps) => {
    const renderButton = (digit: string) => {
        return (
            <button
                key={'live-pad-digit-' + String(digit)}
                className="flex size-12 cursor-pointer items-center justify-center rounded-xl bg-gray-600 text-gray-200"
                onClick={(event: React.MouseEvent) =>
                    clickHandler(event, digit)
                }
            >
                {digit}
            </button>
        );
    };

    return (
        <div className="flex flex-col gap-y-2 pt-3">
            <div className="flex gap-x-2">
                {['1', '2', '3'].map(renderButton)}
            </div>
            <div className="flex gap-x-2">
                {['4', '5', '6'].map(renderButton)}
            </div>
            <div className="flex gap-x-2">
                {['7', '8', '9'].map(renderButton)}
            </div>
            <div className="flex gap-x-2">
                {['*', '0', '#'].map(renderButton)}
            </div>
        </div>
    );
};

const PureDialpadButtons = React.memo(DialpadButtons);

const DtmfDialpad = ({
    handleSendDtmf,
    handleClose,
    number,
    setNumber,
    anchorEl,
}: Props) => {
    const { i18next } = useAppContext();
    const { refs, floatingStyles } = useFloating({
        elements: {
            reference: anchorEl,
        },
        placement: 'top',
        whileElementsMounted: autoUpdate,
        middleware: [shift()],
    });

    const handleTextFieldInput = (
        event: React.ChangeEvent<HTMLInputElement>,
    ) => {
        const input = event.target.value;
        const originalDigits = number;
        const cleanedInput = removeNotAllowedDTMFCharacter(input);
        setNumber(cleanedInput);
        const addedChars = getAdditionalInput(originalDigits, cleanedInput);

        if (handleSendDtmf) {
            try {
                handleSendDtmf(addedChars);
            } catch (error) {
                getFaro()?.api.pushError(error);
                addGeneralLogEntry(`Error reported: ${error}`);
            }
        }
    };

    const getAdditionalInput = (original: string, input: string) => {
        const escapedStarAndHash = original.replace(/\*|#/g, (c) => '\\' + c);
        //A match for the first part of the string, so we can determine
        //what characters have been added to send DTMF signal.
        const currentNumberMatch = new RegExp(`^${escapedStarAndHash}`);

        return input.replace(currentNumberMatch, '');
    };

    const handleButtonClick = (e: React.MouseEvent, digit: string) => {
        setNumber(number.concat(digit));

        if (handleSendDtmf) {
            handleSendDtmf(digit);
        }

        numberInputRef.current.focus();
    };

    const numberInputRef = useRef(null);

    return (
        <div
            ref={refs.setFloating}
            style={floatingStyles}
            className="no-dark z-20"
        >
            <ClickAwayListener onClickAway={handleClose}>
                <div className="mb-6 w-46 rounded-sm bg-gray-800 p-3">
                    <div className="me-auto h-13 justify-items-end">
                        <IconButton
                            aria-label={i18next.t('modal.close')}
                            onClick={handleClose}
                            className="cursor-pointer"
                        >
                            <Icon iconKey="close" className="text-gray-200" />
                        </IconButton>
                    </div>

                    <input
                        ref={numberInputRef}
                        type="text"
                        value={number}
                        className="h-10 w-40 rounded-sm border-none bg-gray-600 px-1 text-gray-200 focus:ring-0 focus:outline-hidden"
                        onChange={handleTextFieldInput}
                        autoFocus={true}
                    />

                    <PureDialpadButtons clickHandler={handleButtonClick} />
                </div>
            </ClickAwayListener>
        </div>
    );
};

export default React.memo(DtmfDialpad);
